import { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSeekerAuth } from "@/context/SeekerAuthContext";
import { useBranding, BrandLogo } from "@/context/BrandingContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { formatApiError, api } from "@/lib/api";
import { Camera, FileUp, ArrowRight, Check, SkipForward } from "lucide-react";

export function SeekerLogin() {
    const { login } = useSeekerAuth();
    const branding = useBranding();
    const nav = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await login(email, password);
            nav("/seeker/jobs");
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-secondary/40 p-4 sm:p-6">
            <div className="w-full max-w-md">
                <Link to="/" className="flex items-center gap-2 justify-center mb-6 sm:mb-8" data-testid="seeker-login-brand">
                    <BrandLogo size={32} />
                    <span className="font-display font-bold text-xl tracking-tight">{branding.short_name}</span>
                </Link>
                <div className="bg-white border border-border rounded-2xl p-6 sm:p-8 space-y-6 shadow-sm">
                    <div>
                        <div className="text-[11px] uppercase tracking-widest font-mono text-ikb font-medium">Candidate portal</div>
                        <h1 className="font-display font-bold text-2xl sm:text-3xl mt-2">Welcome back</h1>
                        <p className="text-sm text-muted-foreground mt-2 leading-relaxed">Track applications, chat with recruiters, and apply with one tap.</p>
                    </div>
                    <form onSubmit={submit} className="space-y-4">
                        <div>
                            <Label className="text-xs font-semibold text-foreground/80">Email</Label>
                            <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 h-11" data-testid="seeker-login-email" />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold text-foreground/80">Password</Label>
                            <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5 h-11" data-testid="seeker-login-password" />
                        </div>
                        <Button type="submit" disabled={busy} className="w-full bg-ikb hover:bg-[#001f7a] text-white h-11 font-medium" data-testid="seeker-login-submit">
                            {busy ? "Signing in..." : "Sign in"}
                        </Button>
                    </form>
                    <div className="text-xs text-foreground/80 text-center">
                        No account? <Link to="/seeker/signup" className="text-ikb hover:underline font-medium">Create one →</Link>
                    </div>
                    <div className="text-xs text-foreground/70 text-center">
                        Forgot password? <Link to="/forgot-password" className="text-ikb hover:underline">Reset</Link>
                    </div>
                </div>
            </div>
        </div>
    );
}

export function SeekerSignup() {
    const { register } = useSeekerAuth();
    const branding = useBranding();
    const nav = useNavigate();
    const [step, setStep] = useState(1);
    const [form, setForm] = useState({
        name: "", email: "", password: "",
        phone: "", location: "", summary: "",
    });
    const [photoFile, setPhotoFile] = useState(null);
    const [photoPreview, setPhotoPreview] = useState(null);
    const [resumeFile, setResumeFile] = useState(null);
    const [busy, setBusy] = useState(false);
    const photoRef = useRef(null);
    const resumeRef = useRef(null);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submitStep1 = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await register({ name: form.name, email: form.email, password: form.password });
            setStep(2);
            toast.success("Account created. Let's complete your profile.");
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    const onPhoto = (f) => {
        if (!f) return;
        if (!f.type.startsWith("image/")) return toast.error("Please pick an image file");
        if (f.size > 2 * 1024 * 1024) return toast.error("Max photo size is 2 MB");
        setPhotoFile(f);
        setPhotoPreview(URL.createObjectURL(f));
    };

    const uploadProfileExtras = async () => {
        setBusy(true);
        try {
            // Upload resume FIRST so the parsed fields seed the profile, then
            // apply user-typed overrides so what they typed in Step 2 wins.
            if (resumeFile) {
                const fd = new FormData();
                fd.append("file", resumeFile);
                await api.post("/seeker/profile/resume", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
            }
            // Patch typed basic fields (overrides parsed values)
            const patchBody = {};
            if (form.phone.trim()) patchBody.phone = form.phone.trim();
            if (form.location.trim()) patchBody.location = form.location.trim();
            if (form.summary.trim()) patchBody.ai_summary = form.summary.trim();
            if (form.name.trim()) patchBody.name = form.name.trim();
            if (Object.keys(patchBody).length) {
                await api.patch("/seeker/profile", patchBody);
            }
            // Upload photo
            if (photoFile) {
                const fd = new FormData();
                fd.append("photo", photoFile);
                await api.post("/seeker/profile-photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
            }
            toast.success("Profile saved. Finding jobs for you…");
            nav("/seeker/jobs");
        } catch (err) {
            toast.error(formatApiError(err));
            // Still land on jobs so a partial failure doesn't trap them
            nav("/seeker/jobs");
        } finally { setBusy(false); }
    };

    const skip = () => nav("/seeker/jobs");

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-secondary/40 p-4 sm:p-6">
            <div className="w-full max-w-xl">
                <Link to="/" className="flex items-center gap-2 justify-center mb-6 sm:mb-8" data-testid="seeker-signup-brand">
                    <BrandLogo size={32} />
                    <span className="font-display font-bold text-xl tracking-tight">{branding.short_name}</span>
                </Link>

                {/* Step indicator */}
                <div className="flex items-center justify-center gap-2 mb-5">
                    <div className={`flex items-center gap-2 ${step >= 1 ? "text-ikb" : "text-muted-foreground"}`}>
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-2 ${step > 1 ? "bg-ikb border-ikb text-white" : step === 1 ? "border-ikb" : "border-border"}`}>
                            {step > 1 ? <Check className="w-3.5 h-3.5" /> : "1"}
                        </div>
                        <span className="text-xs font-medium hidden sm:inline">Account</span>
                    </div>
                    <div className={`h-px w-8 sm:w-16 ${step >= 2 ? "bg-ikb" : "bg-border"}`} />
                    <div className={`flex items-center gap-2 ${step >= 2 ? "text-ikb" : "text-muted-foreground"}`}>
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-2 ${step === 2 ? "border-ikb" : "border-border"}`}>2</div>
                        <span className="text-xs font-medium hidden sm:inline">Profile</span>
                    </div>
                </div>

                <div className="bg-white border border-border rounded-2xl p-6 sm:p-8 shadow-sm">
                    {step === 1 && (
                        <>
                            <div className="mb-6">
                                <div className="text-[11px] uppercase tracking-widest font-mono text-ikb font-medium">Candidate portal</div>
                                <h1 className="font-display font-bold text-2xl sm:text-3xl mt-2">Create your account</h1>
                                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">One profile, every company. Apply with a single tap.</p>
                            </div>
                            <form onSubmit={submitStep1} className="space-y-4">
                                <div>
                                    <Label className="text-xs font-semibold text-foreground/80">Full name</Label>
                                    <Input required value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5 h-11" data-testid="seeker-signup-name" />
                                </div>
                                <div>
                                    <Label className="text-xs font-semibold text-foreground/80">Email</Label>
                                    <Input type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-1.5 h-11" data-testid="seeker-signup-email" />
                                </div>
                                <div>
                                    <Label className="text-xs font-semibold text-foreground/80">Password (min 6 chars)</Label>
                                    <Input type="password" required minLength={6} value={form.password} onChange={(e) => set("password", e.target.value)} className="mt-1.5 h-11" data-testid="seeker-signup-password" />
                                </div>
                                <Button type="submit" disabled={busy} className="w-full bg-ikb hover:bg-[#001f7a] text-white h-11 font-medium group" data-testid="seeker-signup-submit">
                                    {busy ? "Creating..." : <>Continue <ArrowRight className="w-4 h-4 ml-1.5 transition-transform group-hover:translate-x-0.5" /></>}
                                </Button>
                            </form>
                            <div className="text-xs text-muted-foreground text-center mt-5">
                                Already have an account? <Link to="/seeker/login" className="text-ikb hover:underline font-medium">Sign in →</Link>
                            </div>
                        </>
                    )}

                    {step === 2 && (
                        <>
                            <div className="mb-6">
                                <div className="text-[11px] uppercase tracking-widest font-mono text-ikb font-medium">Step 2 of 2 — optional</div>
                                <h1 className="font-display font-bold text-2xl sm:text-3xl mt-2">Complete your profile</h1>
                                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">Add a photo and resume now so you can apply to jobs with one tap. You can always do this later.</p>
                            </div>

                            {/* Photo */}
                            <div className="space-y-2 mb-5">
                                <Label className="text-xs font-semibold text-foreground/80">Profile photo</Label>
                                <div className="flex items-center gap-4">
                                    <button
                                        type="button"
                                        onClick={() => photoRef.current?.click()}
                                        className="w-20 h-20 rounded-full border-2 border-dashed border-border bg-secondary/40 hover:border-ikb hover:bg-secondary flex items-center justify-center overflow-hidden transition-colors"
                                        data-testid="signup-photo-pick"
                                    >
                                        {photoPreview
                                            ? <img src={photoPreview} alt="You" className="w-full h-full object-cover" />
                                            : <Camera className="w-6 h-6 text-muted-foreground" />}
                                    </button>
                                    <div className="text-xs text-muted-foreground leading-relaxed flex-1 min-w-0">
                                        <div className="font-medium text-foreground">PNG, JPG or WebP, up to 2 MB</div>
                                        <div className="mt-0.5">Helps recruiters put a face to the name.</div>
                                    </div>
                                </div>
                                <input ref={photoRef} type="file" accept="image/*" hidden onChange={(e) => onPhoto(e.target.files?.[0])} data-testid="signup-photo-input" />
                            </div>

                            {/* Resume */}
                            <div className="space-y-2 mb-5">
                                <Label className="text-xs font-semibold text-foreground/80">Resume</Label>
                                <button
                                    type="button"
                                    onClick={() => resumeRef.current?.click()}
                                    className="w-full flex items-center justify-between gap-3 px-4 py-3 border-2 border-dashed border-border bg-secondary/40 hover:border-ikb hover:bg-secondary rounded-lg transition-colors text-left"
                                    data-testid="signup-resume-pick"
                                >
                                    <div className="flex items-center gap-3 min-w-0">
                                        <FileUp className="w-5 h-5 text-ikb shrink-0" />
                                        <div className="min-w-0">
                                            <div className="text-sm font-medium truncate">
                                                {resumeFile ? resumeFile.name : "Upload your resume"}
                                            </div>
                                            <div className="text-xs text-muted-foreground mt-0.5">PDF, DOCX, TXT — we'll parse the rest</div>
                                        </div>
                                    </div>
                                    {resumeFile && <Check className="w-5 h-5 text-emerald-600 shrink-0" />}
                                </button>
                                <input ref={resumeRef} type="file" accept=".pdf,.doc,.docx,.txt" hidden onChange={(e) => setResumeFile(e.target.files?.[0] || null)} data-testid="signup-resume-input" />
                            </div>

                            {/* Phone + location */}
                            <div className="grid sm:grid-cols-2 gap-3 mb-5">
                                <div>
                                    <Label className="text-xs font-semibold text-foreground/80">Phone</Label>
                                    <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+91 98765 43210" className="mt-1.5 h-11" data-testid="signup-phone" />
                                </div>
                                <div>
                                    <Label className="text-xs font-semibold text-foreground/80">City</Label>
                                    <Input value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="e.g. Bengaluru" className="mt-1.5 h-11" data-testid="signup-location" />
                                </div>
                            </div>

                            {/* Headline */}
                            <div className="space-y-2 mb-6">
                                <Label className="text-xs font-semibold text-foreground/80">Headline (optional)</Label>
                                <Textarea
                                    value={form.summary}
                                    onChange={(e) => set("summary", e.target.value)}
                                    placeholder="A line or two on what you do and what you're looking for next."
                                    className="min-h-[80px]"
                                    data-testid="signup-summary"
                                />
                            </div>

                            <div className="flex items-center justify-between gap-3">
                                <Button
                                    type="button"
                                    variant="ghost"
                                    onClick={skip}
                                    disabled={busy}
                                    className="h-11 text-muted-foreground hover:text-foreground"
                                    data-testid="signup-skip"
                                >
                                    <SkipForward className="w-4 h-4 mr-1.5" /> Skip for now
                                </Button>
                                <Button
                                    type="button"
                                    onClick={uploadProfileExtras}
                                    disabled={busy}
                                    className="bg-ikb hover:bg-[#001f7a] text-white h-11 px-6 font-medium"
                                    data-testid="signup-finish"
                                >
                                    {busy ? "Saving..." : "Finish & browse jobs"}
                                </Button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
